import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useShallowSelector } from '../../app/hooks';
import { CartItemReview } from '../../components/OrderReview/CartItemReview';
import CouponCodeReview from '../../components/OrderReview/CouponCodeReview';
import LoyaltyCouponItemReview from '../../components/OrderReview/LoyaltyCouponItemReview';
import {
  addCouponToCart,
  deleteCoupon,
  deleteHypothesisItem,
  removeLoyaltiesCode,
  setLoyaltyApplyStatus,
  setPosCartItems,
} from '../../reducers/cartSlice';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../../reducers/messagingSlice';
import { sendOrder } from '../../reducers/orderSlice';
import { setCartEmpty } from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import {
  selectCartLoyalties,
  selectValidCartItems,
} from '../../selectors/cart';
import {
  selectCurrentTransactionCoupons,
  selectCurrentTransactionLoyalties,
  selectCustomerName,
  selectIsPosmonReconnected,
} from '../../selectors/order';
import { isValidCartSelector } from '../../utils/cart';
import { formAutoCombos } from '../../reducers/menuSlice';
import {
  selectAutoComboCartItems,
  selectAutoComboCartItemsQuantity,
} from '../../redux/features/autoCombo/autoCombo.selector';
import HypothesisItemReview from '../../components/OrderReview/HypothesisItemReview';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import useMenuUnificationOrderHandlers from '../../hooks/menuUnification/useMenuUnificationOrderHandler.hooks';
import { refreshMUAutoCombinedItems } from '../../redux/features/autoCombo/autoCombo.slice';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';
import LoyaltiesItemReview from '../../components/OrderReview/LoyaltiesItemReview';
import { CartItemAdditionTypes } from '../../constants/event';
import CustomerInfo from '../../components/CustomerInfo/CustomerInfo';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingBottom: '110px',
  },
}));

function OrderReview() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    cartItems,
    cartItemsQuantity,
    loyaltyCouponItem,
    loyaltyApplyStatus,
    loyaltyCode,
    coupons,
    hypothesisCartItems,
    cartItemCollapseStates,
  } = useShallowSelector((state) => state.cart);
  const cartValid = useSelector(isValidCartSelector);
  const validCartItems = useSelector(selectValidCartItems);
  const isPosmonReconnected = useAppSelector(selectIsPosmonReconnected);
  const couponsToArray = Object.values(coupons);
  const cartLoyalties = useAppSelector(selectCartLoyalties);
  const customerName = useAppSelector(selectCustomerName);

  const { isUnifiedMenu, isAutoComboEnabled, areItemsAutoCombined } =
    useAutoCombo();

  const autoCombinedCartItems = useAppSelector(selectAutoComboCartItems);
  const autoCombinedCartItemsQuantity = useAppSelector(
    selectAutoComboCartItemsQuantity
  );
  const isAIMode = useAppSelector(selectIsAIAutoMode);

  const {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  } = useMenuUnificationOrderHandlers();

  useEffect(() => {
    if (Object.values(validCartItems).length > 0) {
      if (!isAutoComboEnabled) {
        dispatch(
          sendOrder({
            ...(isUnifiedMenu
              ? {
                  menuUnificationOrderFailureCallback,
                  menuUnificationOrderSuccessCallback,
                }
              : {}),
          })
        );
      } else {
        if (isUnifiedMenu) {
          dispatch(
            refreshMUAutoCombinedItems({
              menuUnificationOrderFailureCallback,
              menuUnificationOrderSuccessCallback,
            })
          );
        } else {
          // Handle send order inside formAutoCombos when auto combo is enabled
          dispatch(formAutoCombos());
        }
      }
    } else {
      if (isAutoComboEnabled && !isUnifiedMenu) {
        dispatch(formAutoCombos());
      }
    }
  }, [
    cartItems,
    cartItemsQuantity,
    cartValid,
    validCartItems,
    coupons,
    isAutoComboEnabled,
  ]);

  useEffect(() => {
    if (
      Object.values(validCartItems).length > 0 &&
      loyaltyCouponItem &&
      !loyaltyCouponItem.isApplied
    ) {
      dispatch(
        sendOrder({
          ...(isUnifiedMenu
            ? {
                menuUnificationOrderFailureCallback,
                menuUnificationOrderSuccessCallback,
              }
            : {}),
        })
      );
    }
  }, [loyaltyCouponItem]);

  useEffect(() => {
    dispatch(
      sendOrder({
        ...(isUnifiedMenu
          ? {
              menuUnificationOrderFailureCallback,
              menuUnificationOrderSuccessCallback,
            }
          : {}),
      })
    );
  }, [cartLoyalties]);

  useEffect(() => {
    if (Object.values(validCartItems).length > 0 && isPosmonReconnected) {
      dispatch(
        sendOrder({
          ...(isUnifiedMenu
            ? {
                menuUnificationOrderFailureCallback,
                menuUnificationOrderSuccessCallback,
              }
            : {}),
        })
      );
    }
  }, [isPosmonReconnected]);

  useEffect(() => {
    dispatch(
      setCartEmpty(
        !Object.values({ ...cartItems, ...hypothesisCartItems }).length
      )
    );
    if (isUnifiedMenu && !Object.values(cartItems).length) {
      dispatch(setPosCartItems([]));
    }
  }, [cartItems, hypothesisCartItems]);

  useEffect(() => {
    if (Object.values(validCartItems).length > 0 && customerName) {
      dispatch(
        sendOrder({
          ...(isUnifiedMenu
            ? {
                menuUnificationOrderFailureCallback,
                menuUnificationOrderSuccessCallback,
              }
            : {}),
        })
      );
    }
  }, [customerName]);

  const cartItemsState = useAppSelector(
    (state) => state.order.currentTransactionItems
  );

  const currentTransactionCoupons = useAppSelector(
    selectCurrentTransactionCoupons
  );
  const currentTransactionLoyalties = useAppSelector(
    selectCurrentTransactionLoyalties
  );

  const removeLoyalty = () => {
    dispatch(setLoyaltyApplyStatus(undefined));
  };

  const removeCoupon = (id: string) => {
    dispatch(deleteCoupon(id));
  };

  const cloneCoupon = (code: string) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { message: 'Coupon ' + code + ' added', type: 'METRIC' },
    };
    dispatch(messagingActions.sendInfo(payload as any));
    dispatch(addCouponToCart(code));
  };

  const getCartItemReview = () => {
    const cartItemsToUse = areItemsAutoCombined
      ? autoCombinedCartItems
      : cartItems;
    const cartItemsQuantityToUse = areItemsAutoCombined
      ? autoCombinedCartItemsQuantity
      : cartItemsQuantity;
    const cartItemsIds = Object.keys(cartItemsToUse);
    const hypothesisCartItemsIds = Object.keys(hypothesisCartItems);
    return [...cartItemsIds, ...hypothesisCartItemsIds]
      .map((id) => Number(id))
      .sort((a, b) => a - b)
      .map((cartItemIdInNum) => {
        const cartItemId = cartItemIdInNum.toString();
        const key = uuidv4();
        if (cartItemsIds.includes(cartItemId)) {
          return (
            <CartItemReview
              key={key}
              status={cartItemsState[cartItemId]}
              cartItem={cartItemsToUse[cartItemId]}
              quantity={cartItemsQuantityToUse[cartItemId]}
              collapseState={cartItemCollapseStates[cartItemIdInNum]}
            />
          );
        } else {
          return (
            <HypothesisItemReview
              key={key}
              hypothesisCartItem={hypothesisCartItems[cartItemId]}
              handleHypothesisItemRemove={removeHypothesisItem}
              status={cartItemsState[cartItemId]}
            />
          );
        }
      });
  };

  const removeHypothesisItem = (id: string) => {
    dispatch(deleteHypothesisItem(id));
  };

  const removeOrderLoyalty = (code: string) => {
    dispatch(removeLoyaltiesCode(code));
  };

  return (
    <div id="orderReview" className={classes.container}>
      {customerName && <CustomerInfo customerName={customerName} />}

      {/* 10 digit loyalty code display */}
      {cartLoyalties?.map(({ code, addedBy }) => (
        <LoyaltiesItemReview
          code={code}
          addedBy={addedBy || CartItemAdditionTypes.human}
          status={currentTransactionLoyalties[code]}
          handleLoyaltyRemove={removeOrderLoyalty}
        />
      ))}

      {/* Display loyalty coupon Item */}
      {loyaltyCouponItem && (
        <LoyaltyCouponItemReview item={loyaltyCouponItem} />
      )}

      {/* Display loyalty coupon */}
      {!loyaltyCouponItem && loyaltyApplyStatus && loyaltyCode && (
        <CouponCodeReview
          couponCode={loyaltyCode}
          loyaltyApplyStatus={loyaltyApplyStatus}
          handleLoyaltyRemove={removeLoyalty}
        />
      )}

      {/* Display coupon code */}
      {couponsToArray.length > 0 &&
        couponsToArray.map(({ id, code }) => (
          <CouponCodeReview
            key={id}
            couponCode={code}
            couponId={id}
            status={currentTransactionCoupons[id]}
            handleCouponRemove={removeCoupon}
            handleCouponDuplicate={cloneCoupon}
          />
        ))}
      {getCartItemReview()}
    </div>
  );
}

export default memo(OrderReview);
